"use client";

import { globalStyleObj } from "@/app/assets/styles";
import {
  CommonJoditReactTextEditor,
  CommonTextInputField,
  LabelText,
  LanguageTabs,
  PageRefresh,
  SubmitButton,
  TranslationForm,
} from "@/components";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useErrorToast, useSuccessToast } from "@/lib/hooks";
import { FAQValidationSchema } from "@/schemas/pagesSchema/faq/faqSchema";
import { useAppSelector } from "@/store/hooks";
import { getCustomTheme } from "@/utils/colors";
import { usePageRefresh } from "@/utils/refreshPage";
import { getTranslationDetails } from "@/utils/translation-helper";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEqual } from "lodash";
import { useTheme } from "next-themes";
import { useRouter } from "next/navigation";
import React, { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FaQuestionCircle } from "react-icons/fa";
import { MdCategory, MdErrorOutline } from "react-icons/md";

const FAQManagementForm = ({
  userId,
  languages = [],
  itemDetails = {},
  translationDetails = {},
  categoryList = [],
  createFnc = () => {},
  updateFnc = () => {},
}) => {
  const [activeTab, setActiveTab] = useState("en");
  const { isPending, pageRefresh } = usePageRefresh();
  const { layoutThemePrimaryColorType } = useAppSelector(
    (state) => state.layout
  );
  const { theme } = useTheme();
  const { bgColor, hoverBgColor, textColor, hexCode } = useMemo(
    () => getCustomTheme({ layoutThemePrimaryColorType }),
    [layoutThemePrimaryColorType]
  );
  const isItemDetailsExist = Object.keys(itemDetails).length > 0;
  const activeLanguages = languages.filter((lang) => lang.status);
  const router = useRouter();

  // Handle React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: zodResolver(FAQValidationSchema),
    defaultValues: {
      question: getTranslationDetails(translationDetails, "en")?.question || "",
      answer: getTranslationDetails(translationDetails, "en")?.answer || "",
      category_id: itemDetails?.category_id || "",
      order_number: itemDetails?.order_number
        ? `${itemDetails.order_number + ""}`
        : "1",
    },
  });

  // Relevant fields for comparison with the postDetails object (For Update Post)
  const relevantFields = {
    question: getTranslationDetails(translationDetails, "en")?.question || "",
    answer: getTranslationDetails(translationDetails, "en")?.answer || "",
    category_id: itemDetails?.category_id || "",
    order_number: itemDetails?.order_number
      ? `${itemDetails.order_number + ""}`
      : "1",
  };
  const hasChanges = !isEqual(relevantFields, watch());

  // Handle Validation Errors
  const handleValidationErrors = useCallback(
    (errors) => {
      Object.keys(errors).forEach((field) => {
        setError(field, {
          type: "server",
          message: errors[field].message,
        });
      });
    },
    [setError]
  );

  // Handle Form Submit Functionality
  const onSubmit = async (data) => {
    // For Create FAQ
    if (!isItemDetailsExist) {
      const response = await createFnc(userId, data);

      if (response.success) {
        useSuccessToast(response?.message || "FAQ created successfully.");
        reset();
      } else {
        if (response.errors) {
          handleValidationErrors(response.errors);
        } else {
          useErrorToast(response?.message || "Something went wrong.");
        }
      }
    }

    // For Update FAQ
    else {
      const updatePostResponse = await updateFnc(
        userId,
        itemDetails?._id || "",
        data
      );

      if (updatePostResponse.success) {
        useSuccessToast(
          updatePostResponse?.message || "FAQ updated successfully."
        );
        pageRefresh();
      } else {
        if (updatePostResponse.errors) {
          handleValidationErrors(updatePostResponse.errors);
        } else {
          useErrorToast(updatePostResponse?.message || "Something went wrong.");
        }
      }
    }
  };

  // Handle Translation Form Submit
  const handleTranslationSubmit = async (translateData) => {
    const data = {
      ...watch(),
      translateData: { ...translateData, lang: activeTab },
    };

    await onSubmit(data);
  };

  // Handle Refresh Page Loading when update API runs
  if (isPending) {
    return <PageRefresh />;
  }

  return (
    <div
      className={`${globalStyleObj.backgroundLight900Dark300} mt-[40px] rounded-sm shadow-light overflow-hidden`}
    >
      {isItemDetailsExist && activeLanguages.length > 1 && (
        <LanguageTabs
          languages={activeLanguages}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
        />
      )}

      {activeTab === "en" ? (
        <form onSubmit={handleSubmit(onSubmit)} className={`p-3 sm:p-5`}>
          {/* Question */}
          <CommonTextInputField
            fieldName="question"
            fieldType="text"
            fieldId={`${isItemDetailsExist ? "update-faq-question" : "faq-question"}`}
            control={control}
            errors={errors}
            errorsType={errors?.question}
            placeholderText="Question"
            labelName="Question"
            labelStatus={true}
            translateField={isItemDetailsExist}
          />

          {/* Answer */}
          {/* <CommonTextInputField
            fieldName="answer"
            fieldType="text"
            fieldId={`${isItemDetailsExist ? "update-faq-answer" : "faq-answer"}`}
            control={control}
            errors={errors}
            errorsType={errors?.answer}
            placeholderText="Answer"
            labelName="Answer"
            labelStatus={true}
            translateField={isItemDetailsExist}
            extraClass="mt-5"
          /> */}
          <CommonJoditReactTextEditor
            value={watch("answer")}
            labelText="Answer"
            fieldId={`${isItemDetailsExist ? "update-faq-answer" : "faq-answer"}`}
            fieldName="answer"
            fieldStatus={true}
            onTextEditorChange={(content) => setValue("answer", content)}
            isItemDetailsExist={isItemDetailsExist}
            extraClass="mt-5"
            inputContainerMaxWidth="max-w-[800px]"
          />

          {/* Categories */}
          <div className={`${globalStyleObj.commonInputContainerClass} mt-5`}>
            <LabelText text="Category" htmlForId="faq-category" star={true} />

            <div className="flex flex-col gap-2 w-full max-w-[800px]">
              <Controller
                name="category_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    id="faq-category"
                    value={field.value}
                    onValueChange={(value) => field.onChange(value)}
                  >
                    <SelectTrigger
                      className={globalStyleObj.commonDefaultInputFieldClass}
                    >
                      <SelectValue placeholder="Select FAQ Category" />
                    </SelectTrigger>

                    <SelectContent
                      className={`border-0 ${globalStyleObj.backgroundLight900Dark200}`}
                    >
                      {categoryList.length > 0 ? (
                        categoryList.map((category, index) => (
                          <SelectItem
                            key={category?._id || index}
                            value={category?._id || ""}
                            disabled={
                              category?.default_status || !category?.status
                            }
                            className={`text-[14px] font-poppins-rg text-dark-weight-600 dark:text-light-weight-550 cursor-pointer ${
                              category?.default_status
                                ? "bg-red-500/20"
                                : !category?.status
                                  ? "opacity-70"
                                  : ""
                            }`}
                          >
                            {category?.name || "N/A"}
                          </SelectItem>
                        ))
                      ) : (
                        <p className="text-light-weight-400 text-[13px] font-poppins-rg flex items-center justify-center gap-2">
                          <MdErrorOutline size={16} color="#878a99" />
                          No FAQ Categories Found
                        </p>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />

              {errors && errors?.category_id && (
                <p className={globalStyleObj.commonInputErrorClass}>
                  {errors.category_id.message}
                </p>
              )}
            </div>
          </div>

          {/* FAQ Order Number */}
          <CommonTextInputField
            fieldName="order_number"
            fieldType="text"
            fieldId={`${isItemDetailsExist ? "update-faq-order-number" : "faq-order-number"}`}
            control={control}
            errors={errors}
            errorsType={errors?.order_number}
            placeholderText="Order Number"
            labelName="Order Number"
            labelStatus={false}
            extraClass="mt-5"
            extraInformationText="Use higher number for higher order. Default is 1."
          />

          <SubmitButton
            isSubmitting={isSubmitting}
            hasChanges={hasChanges}
            isDetailsExist={isItemDetailsExist}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
            label={isItemDetailsExist ? "Update FAQ" : "Create FAQ"}
            icon={<FaQuestionCircle />}
          />
        </form>
      ) : (
        <TranslationForm
          fields={[
            {
              name: "question",
              lableName: "Question",
              value:
                getTranslationDetails(translationDetails, activeTab)
                  ?.question || "",
            },
            {
              name: "answer",
              lableName: "Answer",
              value:
                getTranslationDetails(translationDetails, activeTab)?.answer ||
                "",
              isTextEditorType: true,
            },
          ]}
          isSubmitting={isSubmitting}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
          handleTranslationSubmit={handleTranslationSubmit}
        />
      )}
    </div>
  );
};

export default FAQManagementForm;
